import React, { FC, useState } from 'react';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { Button } from '@jobcost-monorepo/ui';
import { useFormContext } from 'react-hook-form';
import { createRepository } from '../../../dto/repositoryFactory';
import {
  ICustomer,
  IJob,
  IListCustomerFetchParams,
  IListJobFetchParams,
  IListOrderCounterFetchParams,
  IOrderCounter,
} from '../../../dto';
import {
  ApiResponse,
  IListResponse,
  MN_CUSTOMERS,
  MN_JOBS,
  MN_ORDER_COUNTERS,
} from '../../../common';
import { AxiosError } from 'axios';
import { handleAxiosError } from '../../../utils';
import dayjs from '@jobcost-monorepo/dayjs-config';

const GenerateOrderCodeButton: FC = () => {
  const { t } = useTranslation('orders');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setValue, watch } = useFormContext();
  const jobId = watch('jobId');
  const startDate = watch('startDate');

  const generateOrderCode = async () => {
    setIsLoading(true);
    const repository = createRepository<IJob, IListJobFetchParams>(MN_JOBS);
    const customerId = await repository
      .get(jobId)
      .then((response: ApiResponse<IJob>) => {
        if (response?.data) {
          const job = response?.data;
          return job?.customerId;
        }
      })
      .catch((err: AxiosError) => handleAxiosError(err));

    if (customerId) {
      const repository = createRepository<ICustomer, IListCustomerFetchParams>(
        MN_CUSTOMERS,
      );
      const customer = await repository
        .get(customerId)
        .then((response: ApiResponse<ICustomer>) => {
          if (response?.data) {
            return response?.data;
          }
        })
        .catch((err: AxiosError) => handleAxiosError(err));
      const year = dayjs.utc(startDate).year();
      if (customer && year) {
        const repository = createRepository<
          IOrderCounter,
          IListOrderCounterFetchParams
        >(MN_ORDER_COUNTERS);
        const counter = await repository
          .getMany({ customerId, year })
          .then((response: ApiResponse<IListResponse<IOrderCounter>>) => {
            if (response?.data && response?.data?.totalCount <= 1) {
              if (response?.data?.totalCount === 0) {
                // @ts-ignore
                const newCounter: IOrderCounter = {
                  customerId: customerId,
                  year: year,
                  counter: 1,
                  code: customer.codeOrder ?? '',
                  customer: { id: customerId, name: customer.name },
                };
                return newCounter;
              }
              const newCounter: IOrderCounter = response?.data.data[0];
              return {
                ...newCounter,
                counter: newCounter.counter + 1,
              };
            }
          })
          .catch((err: AxiosError) => handleAxiosError(err));
        if (counter) {
          const updatedCounter = await repository
            .createOrUpdate(counter?.id, { ...counter, customer: undefined })
            .then((response: ApiResponse<IOrderCounter>) => {
              if (response?.data) {
                return response?.data;
              }
            })
            .catch((err: AxiosError) => handleAxiosError(err));
          if (updatedCounter) {
            const orderCode = `${updatedCounter.code && updatedCounter.code.length > 0 ? `${updatedCounter.code}/` : ''}${updatedCounter.counter}/${year}`;
            setValue('code', orderCode);
          }
        }
      }
    }

    setIsLoading(false);
  };

  return (
    <div className="flex flex-col justify-center align-middle space-x-4">
      <Button
        type="button"
        onClick={generateOrderCode}
        disabled={!jobId || !startDate}
        loading={isLoading}
      >
        {t('form.generateOrderCodeButton')}
      </Button>
    </div>
  );
};

export default GenerateOrderCodeButton;
